import { defineStore } from 'pinia';
import axios from 'axios';
import { useSnackbarHandler } from '@/composables/useSnackbarHandler';
const useClientMatterStore = defineStore('clientMatterStore', {
  state: () => {
    const {
      handleError
    } = useSnackbarHandler();
    return {
      matterId: null,
      entryMatterId: null,
      entryMatter: {},
      matter: {},
      currentMatterRequestToken: null,
      currentEntryMatterRequestToken: null,
      currentMatterWithClientRequestToken: null,
      clientId: null,
      client: {},
      currentClientRequestToken: null,
      handleError
    };
  },
  actions: {
    async getMatterById(matterId, force = false) {
      if (matterId === this.matterId && !force) {
        return this.matter;
      }
      const requestToken = Symbol(`matter-${matterId}`);
      this.currentMatterRequestToken = requestToken;
      const url = `/matters/${matterId}`;
      const response = await axios({
        method: 'GET',
        url
      });

      // Ignore stale responses from earlier matter requests (e.g., A then B where A returns last).
      // Use direct assignment (not $patch) to fully replace the matter object,
      // preventing stale nested properties from a previous matter leaking through.
      if (this.currentMatterRequestToken === requestToken) {
        this.matter = response.data;
        this.matterId = response.data.id;
      }
      return response.data;
    },
    async getMatterStats(matterId) {
      const url = `/matters/${matterId}/stats`;
      const response = await axios({
        method: 'GET',
        url
      });
      return response.data;
    },
    async deleteMatter(matterId) {
      const url = `/matters/${matterId}`;
      await axios({
        method: 'DELETE',
        url
      });
      // Clear cached matter if it's the one we just deleted so a later
      // getMatterById can't return the stale, deleted matter from cache.
      // Compare as strings: route params are strings while API ids may differ
      // in type, and a strict-equality miss would silently keep stale cache.
      if (String(matterId) === String(this.matterId)) {
        this.matter = {};
        this.matterId = null;
      }
    },
    async getMatterByIdForEntryDropdown(entryMatterId, force = false) {
      if (entryMatterId === this.entryMatterId && !force) {
        return this.entryMatter;
      }
      const requestToken = Symbol(`entry-matter-${entryMatterId}`);
      this.currentEntryMatterRequestToken = requestToken;
      const url = `/matters/${entryMatterId}/entry-dropdown`;
      const response = await axios({
        method: 'GET',
        url
      });

      // Ignore stale responses from earlier entry-matter requests.
      if (this.currentEntryMatterRequestToken === requestToken) {
        this.$patch({
          entryMatter: response.data,
          entryMatterId: response.data.id
        });
      }
      return response.data;
    },
    async getMatterWithClientById(matterId) {
      if (matterId === this.matterId) {
        return this.matter;
      }
      const requestToken = Symbol(`matter-with-client-${matterId}`);
      this.currentMatterWithClientRequestToken = requestToken;
      const url = `/matters/${matterId}/matterwithclient`;
      const response = await axios({
        method: 'GET',
        url
      });

      // Ignore stale responses from earlier matter-with-client requests.
      if (this.currentMatterWithClientRequestToken === requestToken) {
        this.$patch({
          matter: response.data,
          matterId: response.data.id
        });
      }
      return response.data;
    },
    async getMatterUsers(matterId) {
      const url = `/matters/${matterId}/users`;
      const response = await axios({
        method: 'GET',
        url
      });
      return response.data;
    },
    async getClientById(clientId, force = false) {
      if (clientId === this.clientId && !force) {
        return this.client;
      }
      const requestToken = Symbol(`client-${clientId}`);
      this.currentClientRequestToken = requestToken;
      const url = `/clients/${clientId}`;
      const response = await axios({
        method: 'GET',
        url
      });

      // Ignore stale responses from earlier requests (e.g., A then B where A returns last).
      // Use direct assignment (not $patch) to fully replace the client object,
      // preventing stale properties from a previous client leaking through.
      if (this.currentClientRequestToken === requestToken) {
        this.client = response.data;
        this.clientId = response.data.id;
      }
      return response.data;
    },
    async updateMatter({
      id,
      update
    }) {
      const url = `/matters/${id}`;
      const response = await axios({
        method: 'PUT',
        url,
        data: update
      });

      // Defensive: only update if we're still viewing this matter.
      // This prevents a stale response from overwriting a newly-selected matter's data.
      // Use direct assignment (not $patch) to fully replace the matter object,
      // so cleared/absent fields don't retain their previous values.
      // Compare as strings: `id` often arrives from route params (string) while
      // `this.matterId` comes from the API response; a strict-equality mismatch
      // would make a successful save return null and leave the store stale.
      if (String(id) === String(this.matterId)) {
        this.currentMatterRequestToken = null;
        this.matter = response.data;
        this.matterId = response.data.id;
        return response.data;
      }

      // User navigated away before response arrived; don't return stale data
      return null;
    },
    async updateClient({
      id,
      update
    }) {
      const url = `/clients/${id}`;
      const response = await axios({
        method: 'PUT',
        url,
        data: update
      });

      // Defensive: only update if we're still viewing this client.
      // This prevents a stale response from overwriting a newly-selected client's data.
      // Use direct assignment (not $patch) to fully replace the client object.
      if (id === this.clientId) {
        // Invalidate any in-flight getClientById request so its stale
        // response doesn't overwrite this freshly-saved data.
        this.currentClientRequestToken = null;
        this.client = response.data;
        this.clientId = response.data.id;
        return response.data;
      }

      // User navigated away before response arrived; don't return stale data
      return null;
    },
    async createClient({
      create
    }) {
      try {
        const url = `/clients`;
        const response = await axios({
          method: 'POST',
          url,
          data: create
        });
        this.currentClientRequestToken = null;
        this.client = response.data;
        this.clientId = response.data.id;
        return response.data;
      } catch (error) {
        this.handleError(error);
        return false;
      }
    },
    async createMatter({
      create
    }) {
      try {
        const url = `/matters`;
        const response = await axios({
          method: 'POST',
          url,
          data: create
        });
        this.$patch({
          matter: response.data,
          matterId: response.data.id,
          entryMatterId: null
        });
        return response.data;
      } catch (error) {
        this.handleError(error);
        throw error;
      }
    }
  }
});
export default useClientMatterStore;